import React from 'react';
import { Button } from 'components/ui';
import {
    isChunkLoadError,
    isStaleChunkReloadInFlight,
    reloadForNewVersion,
    StaleChunkDeps,
} from 'utils/staleChunkRecovery';

interface Props {
    children: React.ReactNode;
    /** Test seam: storage, clock and reload for the stale-chunk loop guard. */
    reloadDeps?: StaleChunkDeps;
}

interface State {
    hasError: boolean;
    error: Error | null;
    /** The failure is a page file that a release replaced, not a bug. */
    stale: boolean;
    /** A guarded reload is on its way. */
    reloading: boolean;
}

/**
 * Last-resort crash screen for the admin.
 *
 * A page file that a release replaced ("Loading chunk N failed") is not a bug:
 * reload once (through the loop guard in utils/staleChunkRecovery) to pick the
 * new release up. If the guard refuses (it just reloaded and the chunk is
 * still missing, or the browser blocks storage) say so, and offer a real
 * reload: "Try Again" is not offered, because React keeps a failed lazy import
 * and would only fail again. Anything else is a bug and looks as before.
 */
class AppErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null, stale: false, reloading: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            stale: isChunkLoadError(error),
            // The reload's own wake: the page is about to be replaced.
            reloading: isStaleChunkReloadInFlight(),
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        if (this.state.reloading) return;
        if (this.state.stale) {
            if (reloadForNewVersion(this.props.reloadDeps)) {
                this.setState({ reloading: true });
                return;
            }
            console.warn('Admin dashboard: a page file was replaced by a release and the page could not reload itself:', error.message);
            return;
        }
        console.error('Admin dashboard error:', error, errorInfo);
    }

    private reloadNow = () => {
        (this.props.reloadDeps?.reload ?? (() => window.location.reload()))();
    };

    render() {
        if (this.state.hasError && this.state.reloading) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Loading the new version of the admin…
                    </p>
                </div>
            );
        }

        if (this.state.hasError && this.state.stale) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
                    <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            This page has been updated
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            A new version of the admin was released while this tab was open. Reload to pick it up.
                        </p>
                        <Button variant="solid" onClick={this.reloadNow}>
                            Reload Page
                        </Button>
                    </div>
                </div>
            );
        }

        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
                    <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
                        <div className="text-4xl mb-4">⚠️</div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            Something went wrong
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            The admin dashboard encountered an unexpected error.
                        </p>
                        <p className="text-xs text-red-500 font-mono mb-6 break-all">
                            {this.state.error?.message}
                        </p>
                        <div className="flex gap-3 justify-center">
                            <Button
                                variant="solid"
                                onClick={() => this.setState({ hasError: false, error: null, stale: false, reloading: false })}
                            >
                                Try Again
                            </Button>
                            <Button
                                variant="default"
                                onClick={this.reloadNow}
                            >
                                Reload Page
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default AppErrorBoundary;
